/**
 * feat-300 — AI edits to one quiz question, by voice or typing.
 *
 * Contract: `decisions/ADR-feat-300-voice-edit.md` §3 (feature root). The type
 * block below is §3.3 pasted verbatim; the functions are one per §3.1 endpoint.
 *
 * Every text field that comes back from these endpoints — reviewer turns, the
 * AI's clarifying question, the proposal summary and values — is UNTRUSTED.
 * Render it as a JSX text node. Never `dangerouslySetInnerHTML`.
 *
 * Fake mode: every function short-circuits to `fakeQuestionEditData` when
 * `config.useDummyData` is on, the same gate the other services use.
 */
import { config } from '../config/environment';
import { ApiError, fetchApi } from '../utils/api';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import type { QuizQuestion } from './quizContentAdminService';
import * as fake from './fakeQuestionEditData';

// ============================================================
// ADR §3.3 — pasted verbatim
// ============================================================

export type QuestionEditState =
  | 'working' | 'needs_reply' | 'proposal_ready' | 'failed'
  | 'applied' | 'discarded' | 'superseded';
export type QuestionEditFailure = 'ai_refused' | 'ai_error' | 'ai_invalid_output' | 'timeout';
export type EditableQuestionField =
  | 'question_text' | 'option_a' | 'option_b' | 'option_c' | 'option_d'
  | 'correct_option' | 'explanation' | 'correct_explanation' | 'blank_accepted_variants';
export type InputMode = 'voice' | 'typed';

export interface QuestionEditProposal {
  summary: string;
  /** Only the fields that change. `null` = clear a nullable field. */
  changes: Partial<Record<EditableQuestionField, string | null>>;
  /** The value of each changed field at proposal time: the diff's "before". */
  base: Partial<Record<EditableQuestionField, string | null>>;
  /** True when the AI's changes equal the current values: render "No change needed", no Apply. */
  no_change: boolean;
}

export type QuestionEditAiPayload =
  | { kind: 'clarify'; question: string }
  | { kind: 'proposal'; summary: string };

export interface QuestionEditMessage {
  id: string;
  seq: number;
  author: 'reviewer' | 'ai';
  author_email: string | null;   // reviewer turns only
  input_mode: InputMode | null;  // reviewer turns only
  created_at: string;            // ISO-8601 UTC
  text: string | null;           // reviewer turns only. UNTRUSTED: render as a text node
  ai: QuestionEditAiPayload | null; // ai turns only. UNTRUSTED text inside
}

export interface QuestionEditThread {
  id: string;
  question_item_id: string;
  state: QuestionEditState;              // EFFECTIVE state (timeout/superseded already applied)
  failure_reason: QuestionEditFailure | null;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  working_started_at: string | null;
  closed_by_email: string | null;
  closed_at: string | null;
  clarifies_remaining: number;           // 3 - clarify turns so far, floored at 0
  proposal: QuestionEditProposal | null; // non-null only when state === 'proposal_ready'
  /** proposal_ready only: the live question no longer matches the proposal's base, so Apply will 409. */
  is_stale: boolean;
  message_count: number;
  messages?: QuestionEditMessage[];      // present for open threads in the list, and always from GET /{id}
}

export interface QuestionEditThreadList {
  tenant_id: string;
  approval_required: boolean;  // drives A7's confirm copy
  server_time: string;
  threads: QuestionEditThread[];
  closed_truncated: boolean;
}

export interface QuestionEditApplyResult {
  thread: QuestionEditThread;
  question: QuizQuestion;      // same shape as the learn-content tree's question
  approval_required: boolean;
}

export type QuestionEditErrorCode =
  | 'thread_open' | 'turn_in_progress' | 'thread_closed' | 'not_ready' | 'not_failed'
  | 'stale_base' | 'no_change' | 'question_deleted'
  | 'empty_audio' | 'audio_too_large' | 'audio_too_long' | 'unsupported_audio'
  | 'transcription_unavailable';

export const OPEN_STATES: readonly QuestionEditState[] =
  ['working', 'needs_reply', 'proposal_ready', 'failed'];
export const NEEDS_YOU_STATES: readonly QuestionEditState[] =
  ['needs_reply', 'proposal_ready', 'failed'];

/** The header counter's predicate (spec §8). Count only threads whose question is rendered on the page. */
export const needsYou = (t: QuestionEditThread): boolean => NEEDS_YOU_STATES.includes(t.state);
/** Polling runs while this is true for any thread on the page (AT-18). */
export const isWorking = (t: QuestionEditThread): boolean => t.state === 'working';

// ============================================================
// Helpers (not part of §3.3)
// ============================================================

export const isOpenThread = (t: QuestionEditThread): boolean => OPEN_STATES.includes(t.state);

export interface TranscribeResult {
  text: string;
  duration_seconds: number;
}

/** The §3 error envelope's `code`, from an error any function here throws.
 *  `null` for a network failure or a body without one. */
export function questionEditErrorCode(e: unknown): QuestionEditErrorCode | null {
  if (!(e instanceof ApiError)) return null;
  const body = e.body as { code?: unknown } | null | undefined;
  return body && typeof body.code === 'string' ? (body.code as QuestionEditErrorCode) : null;
}

/** The `thread` some 409s carry (ADR §3.1 "(+ thread)"). */
export function questionEditErrorThread(e: unknown): QuestionEditThread | null {
  if (!(e instanceof ApiError)) return null;
  const body = e.body as { thread?: unknown } | null | undefined;
  return body && body.thread && typeof body.thread === 'object'
    ? (body.thread as QuestionEditThread)
    : null;
}

const enc = encodeURIComponent;

// ============================================================
// Endpoints (ADR §3.1)
// ============================================================

/** #1 — transcribe a clip. The RAW Blob is the body (not multipart, not JSON),
 *  which is why this goes through `authedApiFetch` and not `fetchApi` (which
 *  JSON-stringifies). The audio is never stored anywhere on the client. */
export async function transcribeQuestionEditAudio(
  questionItemId: string,
  blob: Blob,
  signal?: AbortSignal,
): Promise<TranscribeResult> {
  if (config.useDummyData) return fake.fakeTranscribe(blob, signal);
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  const url = `${protocol}://${config.restaurantBackendHost}/api/question-items/${enc(
    questionItemId,
  )}/edit-transcribe`;
  const response = await authedApiFetch(url, {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': blob.type || 'application/octet-stream' },
    body: blob,
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    // The status only — never the body (it is a transcript endpoint).
    const detail =
      body && typeof (body as { detail?: unknown }).detail === 'string'
        ? (body as { detail: string }).detail
        : `API error: ${response.status}`;
    throw new ApiError(detail, response.status, body);
  }
  return response.json();
}

/** #2 — every open thread (with messages) and the newest closed ones (without),
 *  for the tenant. The only call the page makes on load and on each poll. */
export async function listQuestionEditThreads(
  tenantId: string,
  scope: 'all' | 'open' = 'all',
): Promise<QuestionEditThreadList> {
  if (config.useDummyData) return fake.fakeListThreads(tenantId, scope);
  return fetchApi<QuestionEditThreadList>(
    `/api/tenant/${enc(tenantId)}/question-edit-threads?scope=${scope}`,
    { headers: authHeader() },
  );
}

/** #3 — one thread with its messages (expanding a history row). */
export async function getQuestionEditThread(threadId: string): Promise<QuestionEditThread> {
  if (config.useDummyData) return fake.fakeGetThread(threadId);
  return fetchApi<QuestionEditThread>(`/api/question-edit-threads/${enc(threadId)}`, {
    headers: authHeader(),
  });
}

/** #4 — open a thread with the first reviewer message. 202 → state `working`. */
export async function openQuestionEditThread(
  questionItemId: string,
  text: string,
  inputMode: InputMode,
): Promise<QuestionEditThread> {
  if (config.useDummyData) return fake.fakeOpenThread(questionItemId, text, inputMode);
  return fetchApi<QuestionEditThread>(`/api/question-items/${enc(questionItemId)}/edit-threads`, {
    method: 'POST',
    headers: authHeader(),
    body: { text, input_mode: inputMode },
  });
}

/** #5 — a reviewer reply. 202 → state `working`. */
export async function replyQuestionEditThread(
  threadId: string,
  text: string,
  inputMode: InputMode,
): Promise<QuestionEditThread> {
  if (config.useDummyData) return fake.fakeReply(threadId, text, inputMode);
  return fetchApi<QuestionEditThread>(`/api/question-edit-threads/${enc(threadId)}/messages`, {
    method: 'POST',
    headers: authHeader(),
    body: { text, input_mode: inputMode },
  });
}

/** #6 — re-run the last turn of a failed thread. 202 → state `working`. */
export async function retryQuestionEditThread(threadId: string): Promise<QuestionEditThread> {
  if (config.useDummyData) return fake.fakeRetry(threadId);
  return fetchApi<QuestionEditThread>(`/api/question-edit-threads/${enc(threadId)}/retry`, {
    method: 'POST',
    headers: authHeader(),
  });
}

/** #7 — apply the proposal to the live question. 409 `stale_base` when the
 *  question changed underneath it. */
export async function applyQuestionEditThread(threadId: string): Promise<QuestionEditApplyResult> {
  if (config.useDummyData) return fake.fakeApply(threadId);
  return fetchApi<QuestionEditApplyResult>(`/api/question-edit-threads/${enc(threadId)}/apply`, {
    method: 'POST',
    headers: authHeader(),
  });
}

/** #8 — discard. Idempotent on an already-discarded thread. */
export async function discardQuestionEditThread(threadId: string): Promise<QuestionEditThread> {
  if (config.useDummyData) return fake.fakeDiscard(threadId);
  return fetchApi<QuestionEditThread>(`/api/question-edit-threads/${enc(threadId)}/discard`, {
    method: 'POST',
    headers: authHeader(),
  });
}
